import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, UrlSegment } from '@angular/router';
import { ViewportScroller } from '@angular/common';
import { catchError, of } from 'rxjs';
import { NotificationData, showNotification } from 'src/app/shared/components/app-notifications/app-notifications.component';
import { BreadcrumbsIconsEnum } from 'src/app/shared/enums/icons.enum';
import { BgBomCategory, BgBomModel } from 'src/app/shared/models/bg.model';
import { BreadCrumbItem } from 'src/app/shared/models/breadcrumb.model';
import { CardItem } from 'src/app/shared/models/card.model';
import { Client } from 'src/app/shared/models/client';
import { ProjectModel } from 'src/app/shared/models/projectModel';
import { ProjectNode } from 'src/app/shared/models/projectNode';
import { AuthService } from 'src/app/shared/services/auth.service';
import { BackendApiService } from 'src/app/shared/services/backend-api.service';
import { ExcelService } from 'src/app/shared/services/excel.service';
import { IconService } from 'src/app/shared/services/icon.service';
import { PdfService } from 'src/app/shared/services/pdf.service';
import { BgRecentVisitsService } from 'src/app/shared/services/bg-recent-visits.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-region-navigation',
  templateUrl: './region-navigation.component.html',
  styleUrls: ['./region-navigation.component.scss'],
})
export class RegionNavigationComponent implements OnInit {
  public client?: Client;
  public currentPath!: Array<BreadCrumbItem>;
  public selectedLevel?: Array<ProjectNode>;
  public selectedModels?: Array<ProjectModel>;
  public filteredModels?: Array<ProjectModel>;
  public selectedProjectModel?: ProjectModel;
  public selectedModel?: BgBomModel;

  public loadingModelDetails?: boolean;
  public filterOptions: { [key: string]: string[] } = {};
  public activeFilters: { [key: string]: string } = {};
  public searchText: string = '';
  public isFilterSectionVisible: boolean = true;

  // Pagination properties
  public pageSize: number = 30;
  public currentPage: number = 1;
  public paginatedModels: ProjectModel[] = [];
  public totalPages: number = 1;

  sortedCategories?: BgBomCategory[];

  constructor(
    private authService: AuthService,
    private modelService: BackendApiService,
    private excelService: ExcelService,
    private pdfService: PdfService,
    private iconService: IconService,
    private router: Router,
    private route: ActivatedRoute,
    private recentVisitsService: BgRecentVisitsService,
    private viewportScroller: ViewportScroller
  ) {
    this.client = this.authService.getClientDataStructure();
    this.selectedLevel = this.client?.projectStructure;
    this.currentPath = [];
  }

  ngOnInit(): void {
    // Initialize with the first level when the component loads
    if (this.selectedLevel?.length) {
      this.currentPath.push({
        alternativeNodes: this.selectedLevel,
        icon: this.getIcon(this.selectedLevel[0].name),
        name: this.selectedLevel[0].name.toLowerCase(), // 'Region',
      });
    }

    // Process URL to set the correct navigation state
    this.processUrlPath();
  }

  private processUrlPath(): void {
    // Use url to get all segments
    this.route.url.subscribe(segments => {
      this.logDebug('URL segments', segments);

      if (segments.length === 0) {
        // No segments, show top level
        this.navigateHome();
        return;
      }

      // Reset to initial state
      this.selectedLevel = this.client?.projectStructure;
      this.currentPath = [{
        alternativeNodes: this.selectedLevel,
        icon: this.getIcon(this.selectedLevel?.[0]?.name || ''),
        name: this.selectedLevel?.[0]?.name.toLowerCase() || '',
      }];

      // Process path segments in order
      let currentNodes = this.client?.projectStructure;
      let currentNode: ProjectNode | undefined;

      // Process each URL segment to build the path
      for (let i = 0; i < segments.length; i++) {
        const segmentValue = segments[i].path;

        if (!currentNodes) break;

        // Special handling for the last segment - it could be a model ID
        if (i === segments.length - 1) {
          // Check if last parameter matches any model ID in the current node's models
          const lastSegmentIsModelId = currentNode?.models?.some(m => m.id === segmentValue);

          if (lastSegmentIsModelId && currentNode) {
            const model = currentNode.models?.find(m => m.id === segmentValue);
            if (model) {
              this.selectModel(model);
              return; // We're done
            }
          }
        }

        // Find the node that matches the segment
        currentNode = currentNodes.find(n =>
          this.normalizeForUrl(n.name) === segmentValue ||
          this.normalizeForUrl(n.description) === segmentValue
        );

        if (!currentNode) {
          // If we couldn't find a matching node, check if this is a model ID
          // This handles the case where the last segment is a model ID
          if (i === segments.length - 1 && this.selectedModels) {
            const model = this.selectedModels.find(m => m.id === segmentValue);
            if (model) {
              this.selectModel(model);
              return;
            }
          }
          break;
        }

        // We found a matching node, add it to the path
        this.selectNode(currentNode);

        // Move to the next level of nodes or models
        if (currentNode.nodes?.length) {
          currentNodes = currentNode.nodes;
        } else if (currentNode.models?.length) {
          this.selectedModels = currentNode.models;
        } else {
          break;
        }
      }
    });
  }

  // Helper method to normalize strings for URL comparison
  private normalizeForUrl(text: string): string {
    return text.toLowerCase().replace(/\s+/g, '-');
  }

  downloadExcelOrPdf(type: 'excel' | 'pdf'): void {
    const modelsToDownload = this.filteredModels || this.selectedModels;

    showNotification('info', "", `Starting to download ${modelsToDownload?.length} models.`, 5);

    modelsToDownload?.forEach((model, i) => {
      //if (i > 1) return; // todo: temp to decrease number of downloaded model

      const errorMessage = `Error dowloading model ${model.id}`;
      const errorTitle = 'Error'

      this.modelService
        .GetModelById(model.id)
        .pipe(
          catchError(() => {
            showNotification('error', errorTitle, errorMessage);
            return of();
          })
        )
        .subscribe((modelDetails) => {
          const { result: bgBomModel } = modelDetails;
          if (!modelDetails.isSuccess || !bgBomModel) {
            showNotification('error', errorTitle, errorMessage);
            return;
          }
          if (!bgBomModel.modelName || !bgBomModel.bgCategories) {
            showNotification('error', errorTitle, "Invalid downloaded BOM model.");
            return;
          }

          if (type === 'excel') {
            this.excelService.exportModelToExcel(
              bgBomModel.modelName,
              bgBomModel.bgCategories,
              model,
              this.authService.getClientName()
            );
          } else if (type === 'pdf') {
            this.pdfService.exportModelToPdf(
              bgBomModel.modelName,
              bgBomModel.bgCategories,
              this.selectedProjectModel,
              this.authService.getClientName(),
              true
            );
          }
        });
    });
  }

  downloadExcel(): void {
    this.downloadExcelOrPdf('excel');
  }

  downloadPdf(): void {
    this.downloadExcelOrPdf('pdf');
  }

  getClientLastNodeName(): string {
    return this.client?.configuration?.customLastNodeName ?? 'Address';
  }

      //node Contains Models
      //The last node always contains models
      //the model cant be in the middle  of tree
  nodeContainsModels(node: ProjectNode): boolean {
    if (!node.models?.length) {
      return false;
    }
    return node.models.some((model) => model.id);
  }

   showFilter4NodeModels(node: ProjectNode): boolean {
    if (!node.models?.length) {
      return false;
    }

    //nodes more than 20
    if (node.nodes?.length && node.nodes.length > 20) {
      return false;
    }

    return true;
  }

  mapNodeToCard(node: ProjectNode): CardItem {
    return {
      img: node.img ?? '',
      title: node.description,
      click: () => this.selectNode(node),
      isModel: false
    };
  }

  mapModelToCard(model: ProjectModel): CardItem {
    return {
      img: model.img,
      title: model.name,
      click: () => this.selectModel(model),
      isModel: true
    };
  }

  selectNode(node?: ProjectNode) {
    if (!node) {
      this.navigateHome();
      return;
    }

    this.selectedModel = undefined;
    this.loadingModelDetails = false;

    // Scroll to top of the page when navigating to a new level
    this.viewportScroller.scrollToPosition([0, 0]);

    this.currentPath[this.currentPath.length - 1].selectedNode = node;

    if (node.nodes?.length) {
      this.selectedLevel = node.nodes;
      this.selectedModels = undefined;
      this.filteredModels = undefined;
      this.filterOptions = {};
      this.activeFilters = {};
      this.searchText = '';

      this.currentPath.push({
        currentNode: node,
        icon: this.getIcon(this.selectedLevel[0].name),
        name: this.selectedLevel[0].name.toLowerCase(),
        alternativeNodes: node.nodes,
      });

      // Update the URL based on the current path
      this.updateUrlFromPath();
      return;
    }

    if (node.models?.length) {
      this.selectedModels = node.models;
      this.filteredModels = [...node.models];
      this.filterOptions = {};
      this.activeFilters = {};
      this.searchText = '';

      // Reset pagination
      this.currentPage = 1;
      this.updatePagination();

      // Extract filter options from models
      this.initializeFilters(node);

      this.currentPath.push({
        currentNode: node,
        icon: BreadcrumbsIconsEnum.Address,
        name: this.client?.configuration?.customLastNodeName ?? 'Address',
        alternativeModels: node.models,
      });

      // Update the URL based on the current path
      this.updateUrlFromPath();
    }
  }

  private updateUrlFromPath(): void {
    // Build the URL segments from the current path
    const urlSegments = this.currentPath
      .filter(item => item.currentNode) // Only include items with a selected node
      .map(item => this.normalizeForUrl(item.currentNode?.description || ''));

    // If a model is selected, add its ID as the last segment
    const lastItem = this.currentPath[this.currentPath.length - 1];
    if (lastItem?.selectedModel) {
      urlSegments.push(lastItem.selectedModel.id);
    }

    // Navigate to the new URL without triggering a full page reload
    this.router.navigate(['/user', ...urlSegments], {
      replaceUrl: false,
      skipLocationChange: false
    });
  }

  getIcon(name: string) {
    const iconName = this.toTitleCase(name).replace(
      ' ',
      ''
    ) as keyof typeof BreadcrumbsIconsEnum;
    const customIcon = this.iconService.getBreadcrumbsIconByName(iconName);

    // If the custom icon is null, fallback to Region icon
    return customIcon || BreadcrumbsIconsEnum.TypeOfStucture;
  }

  public toTitleCase(str: string) {
    return str.replace(
      /(^\w|\s\w)(\S*)/g,
      (_, m1, m2) => m1.toUpperCase() + m2.toLowerCase()
    );
  }

  hasAnyModels(): boolean {
    if (!this.client?.projectStructure) return false;

    const checkNodeForModels = (nodes: ProjectNode[]): boolean => {
      for (const node of nodes) {
        if (node.models && node.models.length > 0) return true;
        if (node.nodes && checkNodeForModels(node.nodes)) return true;
      }
      return false;
    };

    return checkNodeForModels(this.client.projectStructure);
  }

  navigateTo(index: number) {
    this.currentPath = this.currentPath.slice(0, index + 1);
    this.currentPath[this.currentPath.length - 1].selectedNode = undefined;
    this.currentPath[this.currentPath.length - 1].selectedModel = undefined;

    // Update the URL to reflect the new navigation state
    this.updateUrlFromPath();
  }

  navigateHome() {
    this.selectedLevel = this.client?.projectStructure;
    this.selectedModels = undefined;
    this.selectedModel = undefined;
    this.loadingModelDetails = false;

    // Reset the URL to the base user route
    this.router.navigate(['/user']);
  }

  // Helper method to log debugging information
  private logDebug(message: string, data?: any): void {
    if (!environment.production) {
      console.log(`[RegionNavigation] ${message}`, data || '');
    }
  }

  selectModel(model: ProjectModel) {
    this.logDebug('Selecting model', model);
    this.loadingModelDetails = true;
    this.selectedModel = undefined;
    this.selectedProjectModel = model;

    // Scroll to top of the page
    this.viewportScroller.scrollToPosition([0, 0]);

    // Make sure the model is marked as selected in the current path
    if (this.currentPath.length > 0) {
      this.currentPath[this.currentPath.length - 1].selectedModel = model;
    }

    // Update the URL to include the model ID
    this.updateUrlFromPath();

    // Build a proper path for recent visits that works with the router
    const urlSegments = this.currentPath
      .filter(item => item.currentNode)
      .map(item => this.normalizeForUrl(item.currentNode?.description || ''));

    // Add model ID as the last segment
    urlSegments.push(model.id);

    // Path for recent visits navigation
    const modelPath = `/user/${urlSegments.join('/')}`;

    this.modelService
      .GetModelById(model.id)
      .pipe(
        catchError((error) => {
          console.error('Error loading model:', error);
          this.loadingModelDetails = false;
          return of();
        })
      )
      .subscribe((data) => {
        this.loadingModelDetails = false;
        this.logDebug('Model data received', data);

        const bgBomModel = data.result;
        if (bgBomModel) {
          this.setPathForCategories(bgBomModel);
          this.selectedModel = bgBomModel;

          // Add model to recent visits
          this.recentVisitsService.addVisit({
            id: model.id,
            name: model.name || bgBomModel.modelName || 'Untitled Model',
            path: modelPath,
            type: 'model',
            icon: 'description'
          });
        } else {
          console.error('No model data received or invalid data structure', data);
        }
      });
  }

  setPathForCategories(model: BgBomModel) {
    model.bgCategories?.forEach((c) => {
      c.bgGroupedSubCategories?.forEach((group) => {
        group.bgSubCategories?.forEach((sub) => {
          sub.path = `${c.name}/${group.name}/${sub.name}`;
        });
      });
    });
  }

  // Shows filter only if models are present and any model has subOptions
  showFilters(): boolean {
    if (!this.selectedModels?.length) {
      return false;
    }

    // Check if any model has subOptions
    return this.selectedModels.some(model =>
      model.subOptions && Object.keys(model.subOptions).length > 0
    );
  }

  // Get all filter keys from models
  getFilterKeys(): string[] {
    if (!this.selectedModels?.length) {
      return [];
    }

    const allKeys = new Set<string>();

    this.selectedModels.forEach(model => {
      if (model.subOptions) {
        Object.keys(model.subOptions).forEach(key => allKeys.add(key));
      }
    });

    return Array.from(allKeys);
  }

  // Gets the possible values for a specific filter option
  getFilterValues(filterKey: string): string[] {
    if (!this.filterOptions[filterKey]) {
      // Find all unique values for this filter key across all models
      const values = new Set<string>();

      this.selectedModels?.forEach(model => {
        if (model.subOptions && model.subOptions[filterKey]) {
          values.add(model.subOptions[filterKey]);
        }
      });

      this.filterOptions[filterKey] = Array.from(values);
    }

    return this.filterOptions[filterKey] || [];
  }

  // Apply filtering to models
  applyFilters(): void {
    if (!this.selectedModels) {
      this.filteredModels = [];
      this.paginatedModels = [];
      this.totalPages = 0;
      return;
    }

    // Start with all models
    this.filteredModels = [...this.selectedModels];

    // Apply each active filter
    Object.entries(this.activeFilters).forEach(([key, value]) => {
      if (value !== 'All') {
        this.filteredModels = this.filteredModels?.filter(model =>
          model.subOptions && model.subOptions[key] === value
        );
      }
    });

    // Apply search text filter if present
    if (this.searchText.trim()) {
      const searchLower = this.searchText.toLowerCase().trim();
      this.filteredModels = this.filteredModels?.filter(model =>
        model.name.toLowerCase().includes(searchLower) ||
        model.projectName?.toLowerCase().includes(searchLower) ||
        model.projectNumber?.toLowerCase().includes(searchLower)
      );
    }

    // Reset to first page when filters change
    this.currentPage = 1;
    this.updatePagination();
  }

  // Update pagination based on current page and filtered models
  updatePagination(): void {
    if (!this.filteredModels) return;

    // Calculate total pages
    this.totalPages = Math.ceil(this.filteredModels.length / this.pageSize);

    // Ensure current page is valid
    if (this.currentPage < 1) this.currentPage = 1;
    if (this.currentPage > this.totalPages) this.currentPage = this.totalPages || 1;

    // Get models for current page
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = Math.min(startIndex + this.pageSize, this.filteredModels.length);

    this.paginatedModels = this.filteredModels.slice(startIndex, endIndex);
  }

  // Go to a specific page
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  // Go to next page
  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePagination();
    }
  }

  // Go to previous page
  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePagination();
    }
  }

  // Get page numbers to display in pagination controls
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5; // Show at most 5 page numbers

    if (this.totalPages <= maxPagesToShow) {
      // If there are 5 or fewer pages, show all
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate range around current page
      let start = Math.max(2, this.currentPage - 1);
      let end = Math.min(this.totalPages - 1, this.currentPage + 1);

      // Adjust to show at least 3 pages in the middle
      if (start === 2) end = Math.min(4, this.totalPages - 1);
      if (end === this.totalPages - 1) start = Math.max(this.totalPages - 3, 2);

      // Add ellipsis after first page if needed
      if (start > 2) pages.push(-1); // -1 represents ellipsis

      // Add middle pages
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis before last page if needed
      if (end < this.totalPages - 1) pages.push(-1);

      // Always show last page
      pages.push(this.totalPages);
    }

    return pages;
  }

  // Handle filter change
  onFilterChange(filterKey: string, event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.activeFilters[filterKey] = value;

    // Reset all subsequent filters to 'All'
    const allFilterKeys = this.getFilterKeys();
    const currentIndex = allFilterKeys.indexOf(filterKey);

    if (currentIndex !== -1 && currentIndex < allFilterKeys.length - 1) {
      // Get all filter keys after the current one
      const subsequentFilterKeys = allFilterKeys.slice(currentIndex + 1);

      // Reset each subsequent filter to 'All'
      subsequentFilterKeys.forEach(key => {
        this.activeFilters[key] = 'All';

        // Reset the select element in the DOM
        const selectElement = document.getElementById(`filter-${key}`) as HTMLSelectElement;
        if (selectElement) {
          selectElement.value = 'All';
        }
      });
    }

    // Clear search text if a filter is changed
    this.searchText = '';
    const searchInput = document.getElementById('filter-search') as HTMLInputElement;
    if (searchInput) {
      searchInput.value = '';
    }

    // Reset pagination to first page
    this.currentPage = 1;

    // Apply the updated filters
    this.applyFilters();
  }

  // Handle search input change
  onSearchChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchText = value;
    this.applyFilters();
  }

  // Extract filter options from node and all model subOptions
  private initializeFilters(node: ProjectNode): void {
    // Reset filter options
    this.filterOptions = {};
    this.activeFilters = {};

    // Check if node has subOptions for filter (string array in ProjectNode)
    if (node.subOptions && node.subOptions.length > 0) {
      // Node has subOptions for filtering
      node.subOptions.forEach(option => {
        this.filterOptions[option] = ['All'];
        this.activeFilters[option] = 'All';
      });
    }

    // Extract all possible values for each filter from models
    // Models have subOptions as an object { [key: string]: string }
    node.models?.forEach(model => {
      if (model.subOptions) {
        Object.entries(model.subOptions).forEach(([key, value]) => {
          if (!this.filterOptions[key]) {
            this.filterOptions[key] = ['All'];
            this.activeFilters[key] = 'All';
          }

          if (!this.filterOptions[key].includes(value)) {
            this.filterOptions[key].push(value);
          }
        });
      }
    });
  }

  toggleFilters(): void {
    this.isFilterSectionVisible = !this.isFilterSectionVisible;
  }

  isFilterActive(): boolean {
    // Check if any filter has a value other than 'All'
    return Object.entries(this.activeFilters).some(([_, value]) => value !== 'All') ||
           this.searchText.trim() !== '';
  }

  // Reset all filters to their default state
  resetFilters(): void {
    // Reset all filters to 'All'
    const filterKeys = this.getFilterKeys();
    filterKeys.forEach(key => {
      this.activeFilters[key] = 'All';

      // Reset select elements to 'All'
      const selectElement = document.getElementById(`filter-${key}`) as HTMLSelectElement;
      if (selectElement) {
        selectElement.value = 'All';
      }
    });

    // Clear search text
    this.searchText = '';

    // Also clear the input field directly in the DOM
    const searchInput = document.getElementById('filter-search') as HTMLInputElement;
    if (searchInput) {
      searchInput.value = '';
    }

    // Reset pagination to first page
    this.currentPage = 1;

    // Apply the reset filters
    this.applyFilters();
  }
}
